import {ErrorBoundary} from '@sentry/react';
import {Toaster} from '@sollapay/ui/components';
import {
  I18nDiscardChangesProvider,
  NewVersionNotifier,
  QueryClientProvider,
  ThemeProvider
} from '@sollapay/web-core';

import {PageErrorState} from '@/components/PageErrorState';
import {Auth0Provider} from '@/lib/auth/providers';
import {useDirection} from '@/lib/i18n/hooks';
import {I18nProvider} from '@/lib/i18n/providers';

import {SentryUserSync} from './SentryUserSync';

import type {AppState} from '@auth0/auth0-react';
import type {FC, PropsWithChildren} from 'react';

const ToasterMount: FC = () => {
  const dir = useDirection();
  return <Toaster position={dir === 'rtl' ? 'top-left' : 'top-right'} expand />;
};

export interface ProvidersProps extends PropsWithChildren {
  onAuthRedirectCallback?: (appState?: AppState) => void;
}

export const Providers: FC<ProvidersProps> = ({onAuthRedirectCallback, children}) => {
  return (
    <ThemeProvider>
      <I18nProvider>
        <I18nDiscardChangesProvider>
          <ErrorBoundary fallback={({resetError}) => <PageErrorState refetch={resetError} />}>
            <ToasterMount />
            <NewVersionNotifier />
            <Auth0Provider onRedirectCallback={onAuthRedirectCallback}>
              <SentryUserSync />
              <QueryClientProvider>{children}</QueryClientProvider>
            </Auth0Provider>
          </ErrorBoundary>
        </I18nDiscardChangesProvider>
      </I18nProvider>
    </ThemeProvider>
  );
};
